import { Badge, Button, cn, Spinner, Textarea } from "@qn/ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  Code2,
  Copy,
  Download,
  FileCode2,
  FileText,
  Globe,
  Image as ImageIcon,
  Link2,
  LinkIcon,
  Pencil,
  Presentation,
  Table2,
  Workflow,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  type ArtifactMeta,
  type ArtifactType,
  buildPreviewHtml,
  downloadArtifact,
  isOffice,
  OFFICE_EXT,
  printArtifact,
  renderMermaid,
} from "../lib/artifacts";
import { useFeature } from "../lib/features";
import { useT } from "../lib/i18n";
import { useUI } from "../lib/ui-store";

export const TYPE_ICON: Record<ArtifactType, typeof Code2> = {
  html: Globe,
  svg: ImageIcon,
  markdown: FileText,
  code: Code2,
  mermaid: Workflow,
  document: FileText,
  sheet: Table2,
  slides: Presentation,
};

/** Sandboxed preview. Only `html` artifacts may run scripts, and never with access to this app's origin. */
export function Preview({
  type,
  content,
  className,
}: {
  type: ArtifactType;
  content: string;
  className?: string;
}) {
  const [html, setHtml] = useState<string>("");
  useEffect(() => {
    let live = true;
    (async () => {
      const svg =
        type === "mermaid" ? await renderMermaid(content).catch(() => undefined) : undefined;
      const h = await buildPreviewHtml(type, content, svg);
      if (live) setHtml(h);
    })();
    return () => {
      live = false;
    };
  }, [type, content]);
  if (!html)
    return (
      <div className="grid h-full place-items-center">
        <Spinner />
      </div>
    );
  return (
    <iframe
      title="preview"
      sandbox={type === "html" ? "allow-scripts" : ""}
      srcDoc={html}
      className={cn("size-full border-0 bg-white", className)}
    />
  );
}

export function ArtifactPanel({ id }: { id: string }) {
  const t = useT();
  const sharingOn = useFeature("sharing");
  const qc = useQueryClient();
  const close = () => useUI.getState().openArtifact(null);
  const q = useQuery({
    queryKey: ["artifact", id],
    queryFn: () =>
      api<{
        artifact: ArtifactMeta;
        content: string;
        versions: Array<{ version: number; author: string; createdAt: number }>;
      }>(`/artifacts/${id}`),
  });
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState<"text" | "link" | null>(null);
  const old = useQuery({
    queryKey: ["artifact", id, "v", viewVersion],
    queryFn: () => api<{ content: string }>(`/artifacts/${id}/versions/${viewVersion}`),
    enabled: viewVersion !== null && viewVersion !== q.data?.artifact.version,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset view state when switching artifacts
  useEffect(() => {
    setViewVersion(null);
    setEditing(false);
  }, [id]);

  const save = useMutation({
    mutationFn: (content: string) => api(`/artifacts/${id}`, { method: "PUT", body: { content } }),
    onSuccess: () => {
      setEditing(false);
      setViewVersion(null);
      qc.invalidateQueries({ queryKey: ["artifact", id] });
      qc.invalidateQueries({ queryKey: ["artifacts"] });
    },
  });
  const share = useMutation({
    mutationFn: (on: boolean) =>
      api<{ token?: string }>(`/artifacts/${id}/share`, {
        method: on ? "POST" : "DELETE",
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["artifact", id] }),
  });

  if (q.isLoading || !q.data)
    return (
      <aside className="grid h-full place-items-center border-s border-[var(--line)]">
        <Spinner />
      </aside>
    );
  const a = q.data.artifact;
  const shownVersion = viewVersion ?? a.version;
  const isOld = shownVersion !== a.version;
  const content = isOld ? (old.data?.content ?? "") : q.data.content;
  const Icon = TYPE_ICON[a.type];
  const shareUrl = a.shareToken ? `${window.location.origin}/s/${a.shareToken}` : "";
  const item =
    "flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)] [&_svg]:size-4";

  return (
    <aside
      className="fixed inset-0 z-30 flex flex-col bg-[var(--surface)] lg:static lg:inset-auto lg:z-auto lg:w-[min(46vw,680px)] lg:shrink-0 lg:border-s lg:border-[var(--line)]"
      aria-label={t("artifact.panel")}
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-3">
        <Icon className="size-4 shrink-0 text-[var(--accent-text)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium" dir="auto">
            {a.title}
          </div>
          <div className="text-[11px] text-[var(--fg-faint)]">{t(`artifact.type.${a.type}`)}</div>
        </div>
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 rounded-lg border border-[var(--line)] px-2 py-1 text-xs hover:bg-[var(--surface-3)]"
            >
              v{shownVersion} <ChevronDown className="size-3" />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              align="end"
              className="glass z-50 max-h-72 min-w-44 overflow-y-auto rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)]"
            >
              {q.data.versions.map((v) => (
                <Dropdown.Item
                  key={v.version}
                  onSelect={() => setViewVersion(v.version)}
                  className={item}
                >
                  <span className="font-mono">v{v.version}</span>
                  <span className="text-xs text-[var(--fg-faint)]">
                    {v.author === "user" ? t("artifact.you") : "AI"} ·{" "}
                    {new Date(v.createdAt).toLocaleTimeString()}
                  </span>
                  {v.version === a.version && <Check className="ms-auto" />}
                </Dropdown.Item>
              ))}
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
        <Button size="icon" variant="ghost" aria-label={t("artifact.close")} onClick={close}>
          <X className="size-4" />
        </Button>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[var(--line)] px-3 py-2">
        <Button
          size="sm"
          variant={editing ? "primary" : "outline"}
          disabled={isOld}
          onClick={() => {
            setDraft(content);
            setEditing((e) => !e);
          }}
        >
          <Pencil className="size-3.5" /> {editing ? t("artifact.preview") : t("action.edit")}
        </Button>
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <Button size="sm" variant="outline">
              <Download className="size-3.5" /> {t("artifact.download")}{" "}
              <ChevronDown className="size-3" />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              align="start"
              className="glass z-50 min-w-48 rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)]"
            >
              {isOffice(a.type) ? (
                <Dropdown.Item
                  className={item}
                  onSelect={() => void downloadArtifact(a.type, a.title, content)}
                >
                  <FileCode2 /> {t("artifact.as")} .{OFFICE_EXT[a.type]}
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  className={item}
                  onSelect={() => void downloadArtifact(a.type, a.title, content)}
                >
                  <FileCode2 /> {t("artifact.asSource")}
                </Dropdown.Item>
              )}
              <Dropdown.Item className={item} onSelect={() => void printArtifact(a.type, content)}>
                <FileText /> PDF
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard?.writeText(content);
            setCopied("text");
            setTimeout(() => setCopied(null), 1500);
          }}
        >
          {copied === "text" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}{" "}
          {t("action.copy")}
        </Button>
        {sharingOn && (
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button size="sm" variant="outline">
                {a.shared ? (
                  <LinkIcon className="size-3.5 text-[var(--success)]" />
                ) : (
                  <Link2 className="size-3.5" />
                )}{" "}
                {t("artifact.share")}
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                align="start"
                className="glass z-50 w-72 rounded-xl border border-[var(--line)] p-3 shadow-[var(--shadow)]"
              >
                {a.shared ? (
                  <div className="space-y-2">
                    <p className="text-xs text-[var(--fg-muted)]">{t("artifact.sharedHint")}</p>
                    <input
                      readOnly
                      dir="ltr"
                      value={shareUrl}
                      onFocus={(e) => e.currentTarget.select()}
                      className="w-full rounded-lg border border-[var(--line)] bg-[var(--surface-2)] px-2 py-1.5 font-mono text-xs"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          navigator.clipboard?.writeText(shareUrl);
                          setCopied("link");
                          setTimeout(() => setCopied(null), 1500);
                        }}
                      >
                        {copied === "link" ? (
                          <Check className="size-3.5" />
                        ) : (
                          <Copy className="size-3.5" />
                        )}{" "}
                        {t("action.copy")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => share.mutate(false)}>
                        {t("artifact.unshare")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-[var(--fg-muted)]">{t("artifact.shareHint")}</p>
                    <Button
                      size="sm"
                      variant="primary"
                      loading={share.isPending}
                      onClick={() => share.mutate(true)}
                    >
                      {t("artifact.createLink")}
                    </Button>
                  </div>
                )}
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        )}
        {isOld && (
          <Button
            size="sm"
            variant="primary"
            className="ms-auto"
            loading={save.isPending}
            onClick={() => save.mutate(content)}
          >
            {t("artifact.restore")}
          </Button>
        )}
        {isOld && <Badge tone="warn">{t("artifact.oldVersion")}</Badge>}
      </div>

      <div className="min-h-0 flex-1">
        {editing ? (
          <div className="flex h-full flex-col gap-2 p-3">
            <Textarea
              dir="ltr"
              spellCheck={false}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-0 flex-1 resize-none font-mono text-xs"
            />
            {save.error && (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {save.error.message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => setEditing(false)}>
                {t("action.cancel")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={save.isPending}
                disabled={draft === content}
                onClick={() => save.mutate(draft)}
              >
                {t("artifact.saveVersion")}
              </Button>
            </div>
          </div>
        ) : isOld && old.isLoading ? (
          <div className="grid h-full place-items-center">
            <Spinner />
          </div>
        ) : (
          <Preview type={a.type} content={content} />
        )}
      </div>
    </aside>
  );
}
